import React from 'react';

/**
 * Reusable Input Component.
 * Supports optional prepending of an element (like a dropdown or icon).
 * Unhandled props (like onMouseDown) are spread onto the outermost container div.
 */
const InputWithPrepend = ({
    type = 'text',
    id,
    name,
    label,
    placeholder = '',
    value,
    onChange,
    required = false,
    disabled = false,
    steps = "1",
    className = '', // For input
    labelClassName = '',
    containerClassName = '', // For outer div
    error = '',
    prepend, // Element to prepend
    onKeyDown, // Explicitly pass to input
    ...rest // Rest of the props (like onMouseDown) spread onto the outer div
}) => {
    const inputBaseStyles = 'w-full px-4 py-2 border rounded-lg ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
        'transition-all duration-200 ease-in-out ' +
        'placeholder-gray-400 text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed';

    // Determine error styles
    const errorStyles = error ? 'border-gmail-red' : 'border-gray-medium';

    // Conditional styling for visual rhyme with prepended element
    const inputPrependStyles = prepend
        ? 'rounded-l-none border-l-0 h-12' // Set h-12 to match the dropdown height, remove left border/radius
        : 'h-12'; // Keep height consistent

    return (
        // Apply unhandled props like onMouseDown to the container div
        <div className={`mb-4 ${containerClassName}`} {...rest}>
            {label && (
                <label 
                    htmlFor={id || name} 
                    className={`flex items-center text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1 inline-block" title="Required field">*</span>}
                </label>
            )}

            {/* Container for Input + Prepend Element */}
            <div className="flex items-center">
                {/* Prepend Element */}
                {prepend}

                {/* Main Input Field */}
                <input
                    type={type}
                    steps={steps}
                    id={id || name}
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown} // Explicitly passed
                    required={required}
                    disabled={disabled}
                    className={`${inputBaseStyles} ${errorStyles} ${className} ${inputPrependStyles}`}
                />
            </div>
            {/* Error message display remains outside */}
        </div>
    );
};

export default InputWithPrepend;